import { notificationPreferencesClient } from 'services/api/client';
import type { NotificationPreferencesUpdateRequest, NotificationsFeedResponse, NotificationLastKey } from './types';
import type { NotificationPreferencesResponse } from './schemas';
import { validateNotificationPreferencesResponse } from './validation';
import { validateNotificationsFeedResponse } from './feedValidation';

type GetNotificationsFeedByUserIdParams = {
  userId: string;
  params?: {
    lastKey?: NotificationLastKey;
    limit?: number;
  };
};

export class ApiNotificationsRepository {
  /**
   * Serializes params for the notifications feed API, handling the lastKey object.
   * The lastKey object must be JSON-stringified before URL encoding.
   */
  private static serializeParams(params?: { lastKey?: NotificationLastKey; limit?: number }): string {
    if (!params) return '';

    const searchParams = new URLSearchParams();

    if (params.limit !== undefined) {
      searchParams.set('limit', String(params.limit));
    }

    if (params.lastKey) {
      // Serialize lastKey object as JSON string
      searchParams.set('lastKey', JSON.stringify(params.lastKey));
    }

    return searchParams.toString();
  }

  static async getNotificationPreferences(userId: string) {
    const response = await notificationPreferencesClient.get<NotificationPreferencesResponse>(`/preferences/${userId}`);
    const validatedResponse = validateNotificationPreferencesResponse(response);
    return validatedResponse;
  }

  static async updateNotificationPreferences(userId: string, preferences: NotificationPreferencesUpdateRequest) {
    const response = await notificationPreferencesClient.patch<NotificationPreferencesResponse>(`/preferences/${userId}`, preferences);
    return response;
  }

  static async getNotificationsFeedByUserId({ userId, params }: GetNotificationsFeedByUserIdParams) {
    const paramsString = this.serializeParams(params);
    const response = await notificationPreferencesClient.get<NotificationsFeedResponse>(
      `/feed/${userId}${paramsString ? `?${paramsString}` : ''}`
    );
    const validatedResponse = validateNotificationsFeedResponse(response);
    return validatedResponse;
  }

  static async markNotificationAsRead(notificationId: string) {
    const response = await notificationPreferencesClient.patch<NotificationPreferencesResponse>(`/feed/${notificationId}`, {
      viewed: true,
    });
    return response;
  }
}
